import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';

/**
 * Auth event types for multi-tab synchronization
 * Validates: Requirements 8.1, 8.2, 8.3
 */
export type AuthEventType = 'login' | 'logout' | 'session-expired';

/**
 * Session expiry reasons
 * Validates: Requirements 8.3
 */
export type SessionExpiredReason = 
  | 'SESSION_EXPIRED' 
  | 'OTHER_DEVICE_LOGIN' 
  | 'ADMIN_REVOKE';

export interface AuthEvent {
  type: AuthEventType;
  reason?: SessionExpiredReason | string;
  timestamp: number;
}

/**
 * Multi-tab synchronization hook
 * Validates: Requirements 8.1, 8.2, 8.3, 8.4, 8.5
 */
export function useMultiTabSync() {
  const logout = useAuthStore((state) => state.logout);
  const checkSession = useAuthStore((state) => state.checkSession);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const lastEventTimestamp = useRef<number>(0);

  // Handle auth events from other tabs
  const handleAuthEvent = useCallback((event: AuthEvent) => {
    // Prevent duplicate handling of the same event
    if (event.timestamp <= lastEventTimestamp.current) {
      return;
    }
    lastEventTimestamp.current = event.timestamp;

    const { type, reason } = event;
    
    // Handle logout and session-expired events (Requirement 8.5)
    if (type === 'logout' || type === 'session-expired') {
      console.log('[MultiTabSync] Auth event received:', type, reason);
      
      // Clear local state
      logout();
      
      // Redirect to login with reason if available
      const loginUrl = reason 
        ? `/login?reason=${encodeURIComponent(reason)}`
        : '/login';
      window.location.href = loginUrl;
    }
    
    // Handle login events (Requirement 8.1)
    if (type === 'login') {
      console.log('[MultiTabSync] Login event received, refreshing session');
      // Re-check session to get updated user data
      checkSession();
    }
  }, [logout, checkSession]);

  useEffect(() => {
    let cleanupStorage: (() => void) | null = null;

    // Try BroadcastChannel first (Requirement 8.1, 8.2, 8.3)
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channelRef.current = new BroadcastChannel('auth');
        
        channelRef.current.onmessage = (event: MessageEvent<AuthEvent>) => {
          handleAuthEvent(event.data);
        };
        
        console.log('[MultiTabSync] BroadcastChannel initialized');
      } else {
        throw new Error('BroadcastChannel not supported');
      }
    } catch {
      // BroadcastChannel not supported, use localStorage fallback (Requirement 8.4)
      console.log('[MultiTabSync] Using localStorage fallback');
      
      const handleStorageChange = (event: StorageEvent) => {
        if (event.key === 'auth-event' && event.newValue) {
          try {
            const data: AuthEvent = JSON.parse(event.newValue);
            handleAuthEvent(data);
          } catch {
            // Ignore parse errors
          }
        }
      };

      window.addEventListener('storage', handleStorageChange);
      cleanupStorage = () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }

    // Cleanup function
    return () => {
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
      if (cleanupStorage) {
        cleanupStorage();
      }
    };
  }, [handleAuthEvent]);
}

/**
 * Broadcast auth event to other tabs
 * Validates: Requirements 8.1, 8.2, 8.3
 */
export function broadcastAuthEvent(
  type: AuthEventType, 
  reason?: SessionExpiredReason | string
): void {
  const event: AuthEvent = {
    type,
    reason,
    timestamp: Date.now(),
  };

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const authChannel = new BroadcastChannel('auth');
      authChannel.postMessage(event);
      authChannel.close();
      console.log('[MultiTabSync] Broadcast event:', type, reason);
    } else {
      throw new Error('BroadcastChannel not supported');
    }
  } catch {
    // BroadcastChannel not supported, use localStorage fallback (Requirement 8.4)
    localStorage.setItem('auth-event', JSON.stringify(event));
    console.log('[MultiTabSync] Broadcast event via localStorage:', type, reason);
  }
}

/**
 * Hook to listen for auth error events (for toast notifications)
 * Validates: Requirements 10.4
 */
export function useAuthErrorListener(onError: (message: string) => void) {
  useEffect(() => {
    const handleAuthError = (event: CustomEvent<{ message: string }>) => {
      onError(event.detail.message);
    };

    window.addEventListener('auth-error', handleAuthError as EventListener);
    
    return () => {
      window.removeEventListener('auth-error', handleAuthError as EventListener);
    };
  }, [onError]);
}
